import { useState, useEffect, FormEvent } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Alert } from '@/components/ui/Alert';
import type { Database } from '@/types/database.types';

type Milestone = Database['public']['Tables']['milestones']['Row'];
type MilestoneType = 'project_milestone' | 'external_dependency' | 'delivery_gate';

interface MilestoneFormProps {
  /** Whether the form modal is open */
  isOpen: boolean;
  /** Callback when form is closed/cancelled */
  onClose: () => void;
  /** Callback when form is submitted with valid data */
  onSubmit: (data: { name: string; target_date: string; type: MilestoneType; description?: string }) => void;
  /** Existing milestone data for edit mode (undefined for create mode) */
  initialData?: Milestone;
  /** Whether the submit action is in progress */
  isLoading?: boolean;
  /** Error message from submission */
  error?: string | null;
}

const MILESTONE_TYPES: { value: MilestoneType; label: string }[] = [
  { value: 'project_milestone', label: 'Project Milestone' },
  { value: 'external_dependency', label: 'External Dependency' },
  { value: 'delivery_gate', label: 'Delivery Gate' },
];

/**
 * Milestone form component for creating and editing milestones
 * Handles both create and edit modes based on presence of initialData
 */
export const MilestoneForm = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isLoading = false,
  error = null,
}: MilestoneFormProps) => {
  const isEditMode = !!initialData;

  // Form state
  const [formData, setFormData] = useState<{
    name: string;
    target_date: string;
    type: MilestoneType;
    description: string;
  }>({
    name: initialData?.name ?? '',
    target_date: initialData?.target_date ?? '',
    type: (initialData?.type as MilestoneType) ?? 'project_milestone',
    description: initialData?.description ?? '',
  });

  // Validation errors
  const [errors, setErrors] = useState<{
    name?: string;
    target_date?: string;
  }>({});

  // Reset form state when initialData changes (e.g. switching between editing different milestones)
  useEffect(() => {
    setFormData({
      name: initialData?.name ?? '',
      target_date: initialData?.target_date ?? '',
      type: (initialData?.type as MilestoneType) ?? 'project_milestone',
      description: initialData?.description ?? '',
    });
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?.id]);

  // Handle input changes
  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // Validate form data
  const validateForm = (): boolean => {
    const newErrors: { name?: string; target_date?: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.target_date) {
      newErrors.target_date = 'Target date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Submit valid data
    onSubmit({
      name: formData.name.trim(),
      target_date: formData.target_date,
      type: formData.type,
      description: formData.description.trim() || undefined,
    });
  };

  // Reset form when modal opens/closes
  const handleClose = () => {
    setFormData({
      name: initialData?.name ?? '',
      target_date: initialData?.target_date ?? '',
      type: (initialData?.type as MilestoneType) ?? 'project_milestone',
      description: initialData?.description ?? '',
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? 'Edit Milestone' : 'Create New Milestone'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* API Error Alert */}
        {error && (
          <Alert variant="error" className="mb-4">
            {error}
          </Alert>
        )}

        {/* Name Field */}
        <div>
          <label htmlFor="milestone-name" className="block text-sm font-medium text-gray-700 mb-1">
            Milestone Name *
          </label>
          <Input
            id="milestone-name"
            type="text"
            placeholder="e.g., Beta Release, QA Complete"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            disabled={isLoading}
            error={errors.name}
            maxLength={200}
          />
        </div>

        {/* Target Date Field */}
        <div>
          <label htmlFor="milestone-date" className="block text-sm font-medium text-gray-700 mb-1">
            Target Date *
          </label>
          <Input
            id="milestone-date"
            type="date"
            value={formData.target_date}
            onChange={(e) => handleChange('target_date', e.target.value)}
            disabled={isLoading}
            error={errors.target_date}
          />
        </div>

        {/* Type Field */}
        <div>
          <label htmlFor="milestone-type" className="block text-sm font-medium text-gray-700 mb-1">
            Type
          </label>
          <Select
            id="milestone-type"
            value={formData.type}
            onChange={(value: string) => handleChange('type', value)}
            disabled={isLoading}
            options={MILESTONE_TYPES.map((t) => ({ value: t.value, label: t.label }))}
          />
        </div>

        {/* Description Field */}
        <div>
          <label htmlFor="milestone-description" className="block text-sm font-medium text-gray-700 mb-1">
            Description (Optional)
          </label>
          <textarea
            id="milestone-description"
            placeholder="Add details about this milestone..."
            value={formData.description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleChange('description', e.target.value)}
            disabled={isLoading}
            maxLength={500}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
          />
          <p className="text-gray-600 text-xs mt-1">
            {formData.description.length}/500 characters
          </p>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : isEditMode ? 'Update Milestone' : 'Create Milestone'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};




